import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, BookOpen, Search, LockKeyhole } from 'lucide-react';
import { api, date } from './api';
import { ErrorNotice, Spinner } from './components';
import type { Lesson, Profile, Summary } from './types';
import type { Navigate } from './App';
import { StudyPlan, AssignmentStart } from './StudyPlan';
import { StudyWorkRow, type StudyWorkItem } from './StudyWork';
import type { StudyScope } from './EducationSelector';

export function Home({ profile, navigate, isAccount = false, registerGuard }: {
  profile: Profile; navigate: Navigate; assignmentsView?: boolean; isAccount?: boolean;
  registerGuard: (fn: null | (() => Promise<void>)) => void;
}) {
  const [data, setData] = useState<{ assignments: Lesson[]; sessions: Summary[]; studyScope?: StudyScope | null } | null>(null);
  const [filter, setFilter] = useState('all'), [search, setSearch] = useState('');
  const [error, setError] = useState(''), [busy, setBusy] = useState('');
  const [educationBusy, setEducationBusy] = useState(false), [educationLocked, setEducationLocked] = useState(false);
  const [allStudies, setAllStudies] = useState(false);
  const childGuard = useRef<null | (() => Promise<void>)>(null);
  const registerChildGuard = useCallback((fn: null | (() => Promise<void>)) => { childGuard.current = fn; }, []);
  const receiveScope = useCallback((scope: StudyScope) => setData(current => current && (!current.studyScope || scope.revision >= current.studyScope.revision) ? { ...current, studyScope: scope } : current), []);
  useEffect(() => {
    registerGuard(async () => {
      if (educationBusy) throw new Error('Wait for your study selection to finish saving.');
      await childGuard.current?.();
    });
    return () => registerGuard(null);
  }, [registerGuard, educationBusy]);
  const load = () => {
    setError('');
    api<NonNullable<typeof data>>('/assignments').then(setData).catch(cause => setError(cause.message));
  };
  useEffect(load, []);
  async function selectStudy(id: string) {
    if (educationBusy || educationLocked || busy || !data?.studyScope?.studies.some(s => s.id === id)) return;
    if (id === data.studyScope.study?.id) { setAllStudies(false); return; }
    setEducationBusy(true); setError('');
    try {
      const next = await api<{ studyScope: StudyScope }>('/account/studies', 'PATCH', { action: 'select', revision: data.studyScope.revision, studyId: id });
      receiveScope(next.studyScope); setAllStudies(false); setFilter('all'); setSearch('');
    } catch (e) { setError((e as Error).message); }
    finally { setEducationBusy(false); }
  }
  async function openWork(id: string) {
    if (busy || educationBusy) return;
    setBusy(id); setError('');
    try { await navigate(`/assignment/${encodeURIComponent(id)}`); }
    catch (cause) { setError((cause as Error).message); }
    finally { setBusy(''); }
  }
  if (!data) return <div className="page">{error ? <ErrorNotice message={error} retry={load}/> : <Spinner/>}</div>;
  const scope = data.studyScope;
  const items = (assignmentIds?: string[], sessionIds?: string[]) => [...new Map<string, StudyWorkItem>([
    ...data.assignments.filter(a => a.kind === 'assignment' && !a.archived && !a.deleted && (!assignmentIds || assignmentIds.includes(a.id)))
      .map(a => [a.id, { id: a.id, title: a.title }] as [string, StudyWorkItem]),
    ...data.sessions.filter(s => !s.archived && !s.deleted && (!sessionIds || sessionIds.includes(s.id))).map(s => [s.assignmentId, {
      id: s.assignmentId, title: s.title, sessionId: s.id, status: s.status, submissionState: s.submissionState,
      updatedAt: s.updatedAt, feedbackAvailable: s.feedbackAvailable ?? Boolean(s.feedback),
    }] as [string, StudyWorkItem]),
  ]).values()];
  const scopedItems = items(scope?.assignmentIds, scope?.sessionIds);
  const work = allStudies ? items() : scopedItems;
  const shown = work.filter(item => item.title.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()) &&
    (filter === 'all' || (filter === 'submitted' ? item.submissionState === 'submitted' : item.submissionState !== 'submitted')));
  const recent = [...work].sort((a, b) => (Date.parse(b.updatedAt ?? '') || 0) - (Date.parse(a.updatedAt ?? '') || 0))[0];
  const target = document.getElementById('portal-context-nav');
  const locked = Boolean(busy) || educationBusy || educationLocked;
  return <div className="page portal-home">
    {target && isAccount && createPortal(<>
      <div className="sidebar-section">YOUR STUDIES</div>
      <nav aria-label="Your studies" aria-busy={educationBusy}>
        <button className="nav-item" aria-current={allStudies ? 'page' : undefined} disabled={locked} onClick={() => setAllStudies(true)}><BookOpen size={18}/><span>All studies</span></button>
        {scope?.studies.filter(study => !study.archived).map(study => <button key={study.id} className="nav-item" aria-current={!allStudies && scope.study?.id === study.id ? 'page' : undefined} disabled={locked} onClick={() => void selectStudy(study.id)}>
          <span className="portal-study-dot" aria-hidden="true"/><span>{study.course}{study.status === 'completed' && <small>Completed</small>}</span>
        </button>)}
      </nav>
      {educationBusy && <p className="portal-nav-hint" role="status">Changing study…</p>}
      {educationLocked && <p className="portal-nav-hint">Save or close your study changes before switching.</p>}
    </>, target)}
    <div className="page-heading">
      <div><div className="eyebrow">MY STUDIES</div><h1>{allStudies ? 'All studies' : scope?.study?.course || 'My studies'}</h1>
        <p className="page-intro">{!allStudies && scope?.study?.institution ? scope.study.institution : 'Your studies and assignments, together.'}</p>
      </div>
      {isAccount && <AssignmentStart disabled={locked} onCreated={openWork}/>}
    </div>
    {isAccount && (scope?.studies.some(study => study.archived) || data.assignments.some(item => item.archived || item.deleted) || data.sessions.some(item => item.archived || item.deleted)) && <p>Archived and deleted work is kept. Open Archived in the Marvin PC app to restore it.</p>}
    {error && <ErrorNotice message={error} retry={load}/>}
    <section className="portal-feature" aria-label="Recent assignment">
      <div><span className="eyebrow">{recent ? 'RECENT ASSIGNMENT' : 'YOUR NEXT STEP'}</span><h2>{recent?.title || 'A place for your next assignment'}</h2>
        <p>{recent?.updatedAt ? `Account copy updated ${date(recent.updatedAt)}.` : 'Create an assignment here, then continue your work in the Marvin PC app.'}</p>
        {recent && <button className="primary" disabled={Boolean(busy) || educationBusy} onClick={() => void openWork(recent.id)}>View assignment <ArrowRight size={16}/></button>}
      </div>
      <div className="portal-feature-note"><LockKeyhole size={21}/><strong>Your work, safely in view</strong><p>Read saved assignments here. Writing, plans, research and AI assistance stay in the PC app.</p><a href="/downloads">Get the PC app <ArrowRight size={14}/></a></div>
    </section>
    <section className="portal-assignments" aria-label="Your assignments">
      <div className="section-heading"><h2>Your assignments <span className="portal-count">{work.length}</span></h2>
        <label className="portal-search"><Search size={16}/><span className="sr-only">Search assignments</span><input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assignments"/></label>
      </div>
      <div className="portal-tabs" role="group" aria-label="Filter assignments">
        {([['all', 'All'], ['active', 'To work on'], ['submitted', 'Submitted']] as const).map(([id, label]) => <button key={id} aria-pressed={filter === id} onClick={() => setFilter(id)}>{label}</button>)}
      </div>
      <div className="portal-assignment-list">
        {shown.length ? shown.map(item => <StudyWorkRow key={item.id} item={item} disabled={Boolean(busy) || educationBusy} onOpen={openWork}/>) : <div className="portal-empty"><h3>{search || filter !== 'all' ? 'No matching assignments' : 'No assignments yet'}</h3><p>{search || filter !== 'all' ? 'Try another search or filter.' : 'Add an assignment above, or manage your studies below to get started.'}</p></div>}
      </div>
    </section>
    {isAccount && <details className="portal-study-details"><summary><span><strong>Manage studies and modules</strong><small>Courses, module status and assignment setup</small></span></summary>
      <StudyPlan key={`study-plan-${profile.id}-${scope?.study?.id}`} registerGuard={registerChildGuard}
        workItems={scopedItems} personalWorkItems={items(scope?.personalAssignmentIds ?? [], scope?.personalSessionIds ?? [])}
        selectedStudy={scope?.study} showSelector={false} selectionBusy={educationBusy} onScopeChanged={receiveScope} onLocked={setEducationLocked} onOpen={openWork}/>
    </details>}
  </div>;
}
